﻿import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, Save, Loader2 } from 'lucide-react';
import { attendanceService, type PunchLogWithUser } from '@/services/attendance.service';
import { DatePicker } from '@/components/ui/date-picker';
import { TimePicker } from '@/components/ui/time-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useInvalidateAttendance } from '@/hooks/attendance/useInvalidateAttendance';

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Returns "YYYY-MM-DD" in IST from an ISO timestamp */
function toISTDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // en-CA gives YYYY-MM-DD
}

/** Returns "HH:MM" (24h) in IST from an ISO timestamp */
function toISTTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Kolkata',
  });
}

// ── Props ──────────────────────────────────────────────────────────────────────

interface Props {
  log:       PunchLogWithUser;
  onClose:   () => void;
  onSuccess: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function EditPunchModal({ log, onClose, onSuccess }: Props) {
  const fullName = [log.first_name, log.last_name].filter(Boolean).join(' ') || '–';

  const [punchType, setPunchType] = useState<'check_in' | 'check_out'>(log.punch_type as 'check_in' | 'check_out');
  const [punchDate, setPunchDate] = useState(toISTDate(log.punch_time));
  const [punchTime, setPunchTime] = useState(toISTTime(log.punch_time));
  const [workMode,  setWorkMode]  = useState(log.work_mode ?? 'office');
  const [reason,    setReason]    = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const invalidateAttendance = useInvalidateAttendance();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Reason for change is required.');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      const punchIso = `${punchDate}T${punchTime}:00+05:30`; // IST offset

      await attendanceService.updatePunch(log.id, {
        punch_type: punchType,
        punch_time: punchIso,
        work_mode:  workMode,
        metadata:   { reason: reason.trim() },
      });

      invalidateAttendance();
      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update punch');
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-modal-nested flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <div className="relative w-full max-w-md rounded-2xl border border-slate-200 dark:border-border bg-white dark:bg-card shadow-2xl">

        {/* Header */}
        <div className="flex items-start gap-3 border-b border-slate-100 dark:border-border/50 px-5 py-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-900/40 mt-0.5">
            <Clock className="h-4 w-4 text-teal-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-slate-800 dark:text-slate-200">Edit Punch Time</h2>
            <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 mt-0.5">{fullName}</p>
            <p className="text-xs text-slate-400 dark:text-slate-500">Update the punch in/out time for this entry.</p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-muted dark:hover:text-slate-300 transition-colors disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">

          {/* Punch Type */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Punch Type
            </label>
            <Select value={punchType} onValueChange={(v) => setPunchType(v as 'check_in' | 'check_out')}>
              <SelectTrigger className="rounded-lg border-slate-300 dark:border-border bg-white dark:bg-muted text-slate-800 dark:text-slate-200 focus:border-teal-500 focus:ring-teal-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="check_in">Check In</SelectItem>
                <SelectItem value="check_out">Check Out</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Date
            </label>
            <DatePicker value={punchDate} onChange={setPunchDate} />
          </div>

          {/* Time */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Time
            </label>
            <TimePicker value={punchTime} onChange={setPunchTime} />
          </div>

          {/* Work Mode */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Work Mode
            </label>
            <Select value={workMode} onValueChange={(v) => setWorkMode(v as 'office' | 'remote' | 'hybrid')}>
              <SelectTrigger className="rounded-lg border-slate-300 dark:border-border bg-white dark:bg-muted text-slate-800 dark:text-slate-200 focus:border-teal-500 focus:ring-teal-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="office">Office</SelectItem>
                <SelectItem value="remote">Remote</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reason */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Reason for Change <span className="text-rose-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              rows={3}
              placeholder="Please provide a reason for editing this punch time…"
              className="w-full resize-none rounded-lg border border-slate-300 dark:border-border bg-white dark:bg-muted px-3 py-2.5 text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-600 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-200"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-lg border border-slate-200 dark:border-border bg-white dark:bg-muted px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-muted transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 rounded-lg bg-teal-700 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-800 transition-colors disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {submitting ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
